/**
 * Foremann AI - Voice Assistant Service
 *
 * Integrates with Gemini API for speech-to-text, natural language understanding,
 * and text-to-speech for hands-free order management.
 */

import { logger } from '../utils/logger';

const API_URL = import.meta.env.VITE_API_URL || 'https://api.gamaleigan.is';

// Web Speech API types (not yet in all TypeScript DOM libs)
interface SpeechRecognitionEvent {
  resultIndex: number;
  results: SpeechRecognitionResultList;
}

interface SpeechRecognitionResultList {
  length: number;
  [index: number]: SpeechRecognitionResult;
}

interface SpeechRecognitionResult {
  isFinal: boolean;
  [index: number]: SpeechRecognitionAlternative;
}

interface SpeechRecognitionAlternative {
  transcript: string;
  confidence: number;
}

interface SpeechRecognitionErrorEvent {
  error: string;
  message?: string;
}

interface SpeechRecognitionInstance {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  start(): void;
  stop(): void;
  onresult: ((event: SpeechRecognitionEvent) => void) | null;
  onend: (() => void) | null;
  onerror: ((event: SpeechRecognitionErrorEvent) => void) | null;
}

interface WindowWithSpeechRecognition extends Window {
  SpeechRecognition?: new () => SpeechRecognitionInstance;
  webkitSpeechRecognition?: new () => SpeechRecognitionInstance;
}

export interface VoiceCommand {
  intent: 'create_order' | 'check_status' | 'update_order' | 'list_orders' | 'help' | 'unknown';
  entities: {
    customer_name?: string;
    customer_id?: string;
    container_id?: string;
    order_type?: 'pickup' | 'delivery' | 'rental' | 'service';
    date?: string;
    time?: string;
    notes?: string;
    order_id?: string;
    confirmation?: string;
  };
  confidence: number;
  raw_text: string;
}

export interface ConversationMessage {
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  audio_url?: string;
}

export interface ForemannState {
  isListening: boolean;
  isProcessing: boolean;
  isSpeaking: boolean;
  currentIntent: VoiceCommand['intent'] | null;
  pendingOrder: Partial<{
    customer_id: string;
    customer_name: string;
    container_id: string;
    type: string;
    scheduled_date: string;
    notes: string;
  }>;
  conversationHistory: ConversationMessage[];
}

// System prompt for Foremann AI
export const FOREMANN_SYSTEM_PROMPT = `Þú ert Foremann, AI aðstoðarmaður fyrir Litla Gámaleigan.
Þú hjálpar starfsmönnum að:
- Stofna nýjar pantanir (tæmingar, afhendingar, leigur, þjónustu)
- Athuga stöðu pantana og gáma
- Uppfæra pantanir
- Finna upplýsingar um viðskiptavini

REGLUR:
1. Talaðu alltaf íslensku
2. Vertu hnitmiðaður og skýr
3. Spyrðu um nauðsynlegar upplýsingar ef þær vantar
4. Staðfestu alltaf pantanir áður en þú stofnar þær
5. Notaðu kurteislegt en faglegt tungumál

TEGUND PANTANA:
- "tæming" eða "sótt" = pickup
- "afhending" = delivery
- "leiga" = rental
- "þjónusta" eða "viðhald" = service

Ef notandi segir eitthvað eins og "Ég þarf tæmingu" eða "Sækja gám", þá er þetta pickup pöntun.
Ef notandi segir "Afhenda gám" eða "Koma með gám", þá er þetta delivery pöntun.

Svaraðu ALLTAF með JSON hlut á þessu formi:
{
  "response": "Textasvar til notanda",
  "intent": "create_order|check_status|update_order|list_orders|help|unknown",
  "entities": {
    "customer_name": "Nafn viðskiptavinar ef nefnt",
    "order_type": "pickup|delivery|rental|service ef greint",
    "date": "YYYY-MM-DD ef dagsetning nefnd",
    "time": "HH:MM ef tími nefndur",
    "notes": "Athugasemdir ef einhverjar"
  },
  "needs_confirmation": true/false,
  "action": "ask_customer|ask_date|ask_type|confirm|execute|none"
}`;

class ForemannAIService {
  private recognition: SpeechRecognitionInstance | null = null;
  private synthesis: SpeechSynthesis | null = null;
  private conversationHistory: ConversationMessage[] = [];
  private onStateChange: ((state: Partial<ForemannState>) => void) | null = null;

  constructor() {
    // Initialize Web Speech API if available
    if (typeof window !== 'undefined') {
      const speechWindow = window as unknown as WindowWithSpeechRecognition;
      const SpeechRecognition = speechWindow.SpeechRecognition || speechWindow.webkitSpeechRecognition;
      if (SpeechRecognition) {
        this.recognition = new SpeechRecognition();
        this.recognition.continuous = false;
        this.recognition.interimResults = true;
        this.recognition.lang = 'is-IS'; // Icelandic
      }
      this.synthesis = window.speechSynthesis;
    }
  }

  setStateCallback(callback: (state: Partial<ForemannState>) => void) {
    this.onStateChange = callback;
  }

  private updateState(state: Partial<ForemannState>) {
    if (this.onStateChange) {
      this.onStateChange(state);
    }
  }

  /**
   * Start listening for voice input
   */
  async startListening(): Promise<string> {
    return new Promise((resolve, reject) => {
      if (!this.recognition) {
        // Fallback: Use Gemini API for speech recognition
        reject(new Error('Raddgreining ekki studd í þessum vafra. Notaðu Chrome.'));
        return;
      }

      this.updateState({ isListening: true });

      let finalTranscript = '';

      this.recognition.onresult = (event: SpeechRecognitionEvent) => {
        for (let i = event.resultIndex; i < event.results.length; i++) {
          if (event.results[i].isFinal) {
            finalTranscript += event.results[i][0].transcript;
          }
        }
      };

      this.recognition.onend = () => {
        this.updateState({ isListening: false });
        resolve(finalTranscript);
      };

      this.recognition.onerror = (event: SpeechRecognitionErrorEvent) => {
        this.updateState({ isListening: false });
        reject(new Error(`Raddgreining mistókst: ${event.error}`));
      };

      this.recognition.start();
    });
  }

  /**
   * Stop listening
   */
  stopListening() {
    if (this.recognition) {
      this.recognition.stop();
    }
    this.updateState({ isListening: false });
  }

  /**
   * Process voice command through Gemini API
   */
  async processCommand(text: string): Promise<VoiceCommand & { response: string; action: string }> {
    this.updateState({ isProcessing: true });

    try {
      // Add user message to history
      this.conversationHistory.push({
        role: 'user',
        content: text,
        timestamp: new Date(),
      });

      // Call backend Foremann API
      const response = await fetch(`${API_URL}/api/foremann/process`, {
        method: 'POST',
        credentials: 'include', // Required for cross-origin cookies (CSRF)
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
        },
        body: JSON.stringify({
          text,
          conversation_history: this.conversationHistory.slice(-10), // Last 10 messages
        }),
      });

      if (!response.ok) {
        throw new Error('Villa við að vinna úr skipun');
      }

      const result = await response.json();

      // Add assistant response to history
      this.conversationHistory.push({
        role: 'assistant',
        content: result.response,
        timestamp: new Date(),
      });

      this.updateState({
        isProcessing: false,
        currentIntent: result.intent,
        conversationHistory: [...this.conversationHistory],
      });

      return result;
    } catch (error) {
      this.updateState({ isProcessing: false });
      throw error;
    }
  }

  /**
   * Speak text using TTS
   */
  async speak(text: string): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!this.synthesis) {
        logger.warn('TTS not available');
        resolve();
        return;
      }

      this.updateState({ isSpeaking: true });

      const utterance = new SpeechSynthesisUtterance(text);
      utterance.lang = 'is-IS';
      utterance.rate = 1.0;
      utterance.pitch = 1.0;

      // Try to find Icelandic voice
      const voices = this.synthesis.getVoices();
      const icelandicVoice = voices.find(v => v.lang.startsWith('is'));
      if (icelandicVoice) {
        utterance.voice = icelandicVoice;
      }

      utterance.onend = () => {
        this.updateState({ isSpeaking: false });
        resolve();
      };

      utterance.onerror = (event) => {
        this.updateState({ isSpeaking: false });
        reject(new Error(`TTS error: ${event.error}`));
      };

      this.synthesis.speak(utterance);
    });
  }

  /**
   * Full voice interaction: Listen -> Process -> Speak response
   */
  async interact(): Promise<{ text: string; response: string; command: VoiceCommand }> {
    const text = await this.startListening();
    const result = await this.processCommand(text);

    // Speak the response
    await this.speak(result.response);

    return {
      text,
      response: result.response,
      command: result,
    };
  }

  /**
   * Clear conversation history
   */
  clearHistory() {
    this.conversationHistory = [];
    this.updateState({ conversationHistory: [] });
  }

  /**
   * Get conversation history
   */
  getHistory(): ConversationMessage[] {
    return [...this.conversationHistory];
  }
}

// Singleton instance
export const foremannAI = new ForemannAIService();

export default foremannAI;
